import { Link } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button, ButtonLink } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Archive, ArrowUpRight, Download, Pencil, Unlink } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useDeleteQRMutation } from "modules/campaigns/hooks/sections/qr/useDeleteQRMutation"
import { useQRsQuery } from "modules/campaigns/hooks/sections/qr/useQRsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { QRsResponseDataSchema } from "schema"
import { z } from "zod"
import { DownloadQRImage } from "./DownloadQRImage"

const columnHelper = createColumnHelper<Features, z.infer<typeof QRsResponseDataSchema>[number]>()

type Props = {
  data: ReturnType<typeof useQRsQuery>["data"]
  isPending: boolean
  selectedIds?: string[]
  initialSelectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
}

export const QRsTable = ({ data, isPending }: Props) => {
  const { t } = useTranslation()
  const { data: employeeData } = useAuthEmployeeQuery()
  const { mutateAsync: mutateDeleteQRAsync, isPending: deleteQRIsPending } = useDeleteQRMutation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("id", {
          header: t("campaigns.templates.qr.table.id"),
        }),
        columnHelper.accessor("name", {
          header: t("campaigns.templates.qr.table.name"),
        }),
        columnHelper.accessor("campaign", {
          header: t("campaigns.templates.qr.table.campaign"),
          cell: (props) => {
            const campaign = props.getValue()

            if (!campaign || !campaign.id) {
              return (
                <Badge variant={"outline"}>
                  <Unlink size={14} />
                  {t("campaigns.templates.qr.table.noCampaign")}
                </Badge>
              )
            }

            const name = campaign.name

            return (
              <Link
                className={cn(
                  "flex font-medium hover:underline",
                  name ? "text-interactive" : "text-muted-foreground",
                )}
                to="/campaigns/$id"
                params={{ id: campaign.id }}
                target="_blank"
              >
                <span>{name ?? t("campaigns.untitled")}</span>
                <ArrowUpRight size={16} />
              </Link>
            )
          },
          meta: {
            colSpan: 2,
          },
        }),
        columnHelper.accessor("scans", {
          header: t("campaigns.templates.qr.table.scans"),
          meta: {
            colSpan: 1,
          },
        }),
        columnHelper.accessor("lastScannedAt", {
          header: t("campaigns.templates.qr.table.lastScannedAt"),
          cell: (props) => {
            const date = props.getValue()

            if (date) {
              return <DateDisplay date={date} />
            }
            return null
          },
          meta: {
            colSpan: 1,
          },
        }),
        columnHelper.display({
          id: "actions",
          cell: (props) => {
            const { id, name } = props.row.original

            return (
              <div className="flex gap-1.5 justify-end opacity-0 group-hover/row:opacity-100">
                <ButtonLink
                  variant={"outline"}
                  size="icon"
                  to="/settings/channels/qr/$id"
                  params={{ id }}
                >
                  <Pencil />
                </ButtonLink>
                <DownloadQRImage qrName={name} qrId={id} companyId={employeeData?.companyId}>
                  <Button variant={"outline"} size="icon">
                    <Download />
                  </Button>
                </DownloadQRImage>
                <Button
                  variant={"outline"}
                  size="icon"
                  onClick={() => mutateDeleteQRAsync({ id })}
                  disabled={deleteQRIsPending}
                >
                  <Archive />
                </Button>
              </div>
            )
          },
        }),
      ]),
    [t, employeeData, deleteQRIsPending, mutateDeleteQRAsync],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.templates.qr.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id} className="group/row duration-150">
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
